// LIGHT COPY of components/topology/TopologyProtocols.jsx for /event-trace-light. The dark original is untouched;
// changes to it are NOT carried here automatically.
import React, { useMemo, useState, useCallback } from 'react';
import ReactFlow, { Controls, Background, BackgroundVariant } from 'reactflow';
import 'reactflow/dist/style.css';
import { KNOWN_DEVICES, EDGE_AGENT_NAME, PROTOCOL_COLORS, fmtUtc, SITE_NATIVE_PREFIX } from '../../constants/topologyLight';
import { deviceTypeLabel, deviceRoleClass, knownDeviceName } from '../../constants/deviceTypes';
import { TOPOLOGY_NODE_TYPES } from './TopologyNodesLight';
import { edgeNodeDisplayName } from '../../utils/edgeNodeDisplayName';
import { eventWindow } from '../../utils/counts';

const ALL_PROTOCOLS = ['all', 'dnp3', 'mms', 'goose', 'modbus', 'snmp', 'it'];

// Count events per protocol
function protocolStats(events) {
  const counts = {};
  events.forEach(ev => {
    const p = (ev.protocol || 'it').toLowerCase();
    counts[p] = (counts[p] || 0) + 1;
  });
  return counts;
}

// Build graph for protocol mode
function buildProtocolGraph(assets, edges, events, selectedProtocols, edgeNode, events24h) {
  const nodes = [];
  const rfEdges = [];
  const hasAll = selectedProtocols.includes('all');

  // Resolve device list first — needed for GOOSE/IED detection below
  const nativePrefix = edgeNode ? (SITE_NATIVE_PREFIX[edgeNode.edge_node_id] || null) : null;
  const deviceAssets = assets.length > 0 ? assets
    : Object.entries(KNOWN_DEVICES)
        .filter(([ip]) => !nativePrefix || ip.startsWith(nativePrefix))
        .map(([ip, kd]) => ({
          ip_address: ip, asset_name: knownDeviceName(ip, kd.name), asset_type: kd.role, vendor: kd.vendor,
          risk_level: null, protocols_used: [],
        }));

  // Use 24h events for GOOSE detection so the bus stays visible between simulation bursts.
  const goosePool = (events24h && events24h.length > 0) ? events24h : events;
  const gooseEvents = goosePool.filter(e => (e.protocol || '').toLowerCase() === 'goose');
  // Show GOOSE bus when live events exist OR when the site has IED-role devices (substation topology).
  const hasIEDs = deviceAssets.some(a => (KNOWN_DEVICES[a.ip_address]?.role || a.asset_type) === 'IED');
  const hasGoose = (gooseEvents.length > 0 || hasIEDs) && (selectedProtocols.includes('goose') || hasAll);
  const rogueGoose = gooseEvents.find(e => {
    const mac = (e.details?.source_mac || '').toLowerCase();
    return mac && !['00:50:56', '08:00:27', 'b8:27:eb'].some(oui => mac.startsWith(oui));
  });

  // Cloud
  nodes.push({
    id: 'cloud', type: 'cloud', position: { x: 280, y: 0 },
    data: { label: 'EdgeNotary Cloud' }, draggable: false,
  });

  // Edge agent
  nodes.push({
    id: 'edge-agent', type: 'edgeAgent', position: { x: 255, y: 110 },
    data: {
      hostname: edgeNode ? edgeNodeDisplayName(edgeNode, EDGE_AGENT_NAME) : EDGE_AGENT_NAME,
      policiesLoaded: edgeNode?.policies_loaded || 0,
      enforcementMode: edgeNode?.enforcement_mode || 'dry-run',
      status: edgeNode?.status || 'Offline',
    },
  });
  rfEdges.push({
    id: 'cloud-agent',
    source: 'cloud', target: 'edge-agent',
    animated: true,
    style: { stroke: '#38BDF8', strokeWidth: 1.5, strokeDasharray: '6 3' },
  });

  const COLS = Math.min(Math.max(deviceAssets.length, 1), 6);
  const COL_GAP = 120;

  // GOOSE bus — positioned above devices when present
  if (hasGoose) {
    nodes.push({
      id: 'goose-bus', type: 'gooseBus', position: { x: 0, y: 380 },
      data: {
        hasActivity: gooseEvents.length > 0,
        rogueSource: rogueGoose?.details?.source_mac || null,
        policyBadge: gooseEvents.length > 0
          ? (gooseEvents.find(e => e.policy_name)?.policy_name || 'MONITORED')
          : null,
        width: 700,
      },
    });
    rfEdges.push({
      id: 'agent-to-goose-bus',
      source: 'edge-agent', target: 'goose-bus',
      animated: gooseEvents.length > 0,
      style: {
        stroke: rogueGoose ? '#EF4444' : '#EAB308',
        strokeWidth: 1.5,
        strokeDasharray: rogueGoose ? '3 2' : undefined,
      },
    });
  }

  // All device nodes in a single dynamic layout
  const deviceY = hasGoose ? 460 : 250;
  deviceAssets.forEach((asset, idx) => {
    const ip = asset.ip_address;
    const kd = KNOWN_DEVICES[ip] || {};
    const row = Math.floor(idx / COLS);
    const col = idx % COLS;
    const rowCount = Math.min(deviceAssets.length - row * COLS, COLS);
    const rowStartX = Math.max(0, 280 - ((rowCount - 1) * COL_GAP) / 2);
    const x = rowStartX + col * COL_GAP;
    const y = deviceY + row * 160;
    const name = asset.asset_name || knownDeviceName(ip, kd.name) || ip;
    const vendor = asset.vendor || kd.vendor || '';
    const storedRole = kd.role || asset.asset_type || 'Device';
    nodes.push({
      id: `node-${ip}`, type: 'otDevice', position: { x, y },
      data: {
        ip,
        name,
        role: deviceTypeLabel(name, vendor, storedRole),
        roleClass: deviceRoleClass(name, vendor, storedRole),
        vendor,
        riskLevel: asset.risk_level || 'normal',
        protocols: asset.protocols_used || [],
      },
    });
    if (hasGoose) {
      rfEdges.push({
        id: `goose-to-${ip}`,
        source: 'goose-bus', target: `node-${ip}`,
        animated: gooseEvents.length > 0,
        style: {
          stroke: rogueGoose ? '#EF444466' : '#EAB30855',
          strokeWidth: 1,
          strokeDasharray: '2 3',
        },
      });
    }
  });

  // Overlay edges from events, filtered by selected protocols, with opacity for non-selected
  edges.forEach((e, idx) => {
    const srcId = `node-${e.source_ip}`;
    const dstId = `node-${e.dest_ip}`;
    const srcExists = nodes.find(n => n.id === srcId);
    const dstExists = nodes.find(n => n.id === dstId);
    if (!srcExists || !dstExists) return;

    const proto = (e.protocols?.[0] || 'it').toLowerCase();
    if (proto === 'goose') return; // GOOSE rendered via bus

    const isSelected = hasAll || selectedProtocols.includes(proto);
    const color = PROTOCOL_COLORS[proto] || '#475569';

    rfEdges.push({
      id: `edge-${idx}`,
      source: srcId, target: dstId,
      animated: isSelected && e.status === 'suspicious',
      style: {
        stroke: color,
        strokeWidth: isSelected ? Math.min(1.5 + e.event_count / 20, 3.5) : 0.5,
        opacity: isSelected ? 0.85 : 0.1,
        strokeDasharray: e.blocked ? '4 2' : undefined,
      },
      label: isSelected ? proto.toUpperCase() : '',
      labelStyle: { fill: color, fontSize: 9 },
      labelBgStyle: { fill: isSelected ? 'rgba(255,255,255,0.95)' : 'transparent' },
    });
  });

  return { nodes, rfEdges };
}

export default function TopologyProtocols({ assets, edges, events, events24h, edgeNode, windowHours = null, eventTotal = null }) {
  const [selectedProtocols, setSelectedProtocols] = useState(['all']);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const stats = useMemo(() => protocolStats(events), [events]);
  // The legend counts the same capped rows as the trace header; say which rows (R-500).
  const legendWindow = useMemo(
    () => eventWindow(events, { hoursBack: windowHours, total: eventTotal }), [events, windowHours, eventTotal]);

  const toggleProtocol = useCallback((proto) => {
    if (proto === 'all') {
      setSelectedProtocols(['all']);
    } else {
      setSelectedProtocols(prev => {
        const without = prev.filter(p => p !== 'all');
        if (without.includes(proto)) {
          const next = without.filter(p => p !== proto);
          return next.length === 0 ? ['all'] : next;
        }
        return [...without, proto];
      });
    }
  }, []);

  const { nodes: rfNodes, rfEdges: rfEdgesData } = useMemo(
    () => buildProtocolGraph(assets, edges, events, selectedProtocols, edgeNode, events24h),
    [assets, edges, events, selectedProtocols, edgeNode, events24h]
  );

  const onEdgeClick = useCallback((_, edge) => {
    const proto = edge.label?.toLowerCase() || '';
    const matchingEvents = events.filter(e => (e.protocol || '').toLowerCase() === proto);
    setSelectedNode(null);
    setSelectedEdge({ edge, proto, events: matchingEvents.slice(0, 10) });
  }, [events]);

  const onNodeClick = useCallback((_, node) => {
    if (node.id === 'cloud' || node.id === 'agent') return;
    const ip = node.data?.ip || node.id;
    const nodeEvents = events.filter(e => e.source_ip === ip || e.destination_ip === ip);
    const kd = KNOWN_DEVICES[ip] || {};
    setSelectedEdge(null);
    const role = deviceTypeLabel(node.data?.name || knownDeviceName(ip, kd.name), node.data?.vendor || kd.vendor, kd.role);
    // The detail heading names the device as its graph node does (registered asset first, then
    // KNOWN_DEVICES): one precedence across the light Event Trace (TopologyAttackChainLight deviceNodeName).
    setSelectedNode({ ip, name: node.data?.name || knownDeviceName(ip, kd.name) || node.data?.label || ip, role, events: nodeEvents });
  }, [events]);

  const gooseEvents = useMemo(() => events.filter(e => (e.protocol || '').toLowerCase() === 'goose'), [events]);
  const rogueGooseEvents = gooseEvents.filter(e => e.details?.source_mac);

  // Policies relevant to selected protocols
  const relevantPolicies = useMemo(() => {
    const protoList = selectedProtocols.includes('all')
      ? events.map(e => (e.protocol || '').toLowerCase())
      : selectedProtocols;
    return [...new Set(
      events
        .filter(e => protoList.includes((e.protocol || '').toLowerCase()))
        .map(e => e.policy_name)
        .filter(Boolean)
    )];
  }, [events, selectedProtocols]);

  return (
    <div className="flex h-full">
      {/* Left rail */}
      <div className="w-60 flex-shrink-0 flex flex-col border-r border-slate-200 bg-white/50">
        <div className="p-3 border-b border-slate-200">
          <div className="text-xs font-semibold text-slate-700 mb-2">Protocol Legend</div>
          {events.length > 0 && (
            <div data-testid="protocols-window" className="text-[10px] text-slate-500 -mt-1 mb-2">{legendWindow.label}</div>
          )}
          <div className="space-y-1.5">
            {ALL_PROTOCOLS.map(proto => {
              const isAll = proto === 'all';
              const color = isAll ? '#475569' : (PROTOCOL_COLORS[proto] || '#475569');
              const isActive = selectedProtocols.includes(proto);
              const count = isAll ? events.length : (stats[proto] || 0);
              return (
                <button
                  key={proto}
                  onClick={() => toggleProtocol(proto)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded transition-colors text-left ${
                    isActive ? 'bg-slate-100/80 ring-1' : 'hover:bg-slate-100/40 opacity-50'
                  }`}
                  style={isActive ? { ringColor: color } : {}}
                >
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                  <span className="text-[12px] font-semibold flex-1" style={{ color: isActive ? color : '#64748B' }}>
                    {proto.toUpperCase()}
                  </span>
                  {count > 0 && (
                    <span className="text-[10px] text-slate-600">{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* GOOSE special status */}
        {gooseEvents.length > 0 && (
          <div className="p-3 border-b border-slate-200">
            <div className="text-[11px] text-slate-600 mb-1.5">GOOSE Domain</div>
            <div className="space-y-1">
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-600">Events</span>
                <span className="text-yellow-700">{gooseEvents.length}</span>
              </div>
              {rogueGooseEvents.length > 0 && (
                <div className="mt-1.5 px-2 py-1 rounded bg-red-50 border border-red-300">
                  <div className="text-[10px] text-red-700 font-bold">ROGUE SOURCE DETECTED</div>
                  <div className="text-[10px] text-red-700 font-mono truncate">{rogueGooseEvents[0].details?.source_mac}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Policy counts from edge node */}
        <div className="p-3 border-b border-slate-200">
          <div className="text-[11px] text-slate-600 mb-1.5">Policies Loaded</div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-bold text-slate-800">{edgeNode?.policies_loaded ?? '—'}</span>
            <span className="text-[11px] text-slate-600">total</span>
            {edgeNode?.policies_enabled != null && (
              <span className="text-[11px] text-emerald-700 ml-1">{edgeNode.policies_enabled} enabled</span>
            )}
          </div>
        </div>

        {/* Policies triggered in current window */}
        {relevantPolicies.length > 0 && (
          <div className="p-3">
            <div className="text-[11px] text-slate-600 mb-1.5">Triggered This Window</div>
            {relevantPolicies.slice(0, 5).map(p => (
              <div key={p} className="text-[11px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-300 mb-1 truncate">{p}</div>
            ))}
            {relevantPolicies.length > 5 && (
              <div className="text-[10px] text-slate-500">+{relevantPolicies.length - 5} more</div>
            )}
          </div>
        )}
      </div>

      {/* Center graph */}
      <div className="flex-1 relative" style={{ background: '#F8FAFC' }}>
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdgesData}
          nodeTypes={TOPOLOGY_NODE_TYPES}
          onEdgeClick={onEdgeClick}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.12 }}
          minZoom={0.3}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Controls className="!bg-slate-100 !border-slate-200" />
          <Background color="#CBD5E1" variant={BackgroundVariant.Dots} gap={20} size={1} />
        </ReactFlow>
        <div className="absolute top-2 left-2 bg-white/80 border border-slate-200 rounded px-2 py-1">
          <span className="text-[10px] text-slate-600">PROTOCOL OVERLAY</span>
        </div>
      </div>

      {/* Right rail */}
      <div className="w-72 flex-shrink-0 flex flex-col border-l border-slate-200 bg-white/50 overflow-y-auto">
        {selectedNode ? (
          <NodeEventDetail node={selectedNode} onClose={() => setSelectedNode(null)} />
        ) : selectedEdge ? (
          <EdgeEventDetail edgeSel={selectedEdge} onClose={() => setSelectedEdge(null)} />
        ) : (
          <ProtocolSummary events={events} gooseEvents={gooseEvents} />
        )}
      </div>
    </div>
  );
}

function NodeEventDetail({ node, onClose }) {
  const { ip, name, role, events } = node;
  const stats = useMemo(() => protocolStats(events), [events]);
  const total = events.length;
  const enforced = events.filter(e => e.enforcement_event).length;
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-bold text-slate-800">{name}</div>
          <div className="text-[11px] text-slate-600 font-mono">{ip}{role ? ` · ${role}` : ''}</div>
        </div>
        <button onClick={onClose} className="text-slate-600 hover:text-slate-700">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="bg-slate-100/60 rounded p-2 text-center">
          <div className="text-lg font-bold text-slate-800">{total}</div>
          <div className="text-[10px] text-slate-600">events</div>
        </div>
        <div className="bg-slate-100/60 rounded p-2 text-center">
          <div className={`text-lg font-bold ${enforced > 0 ? 'text-red-700' : 'text-slate-600'}`}>{enforced}</div>
          <div className="text-[10px] text-slate-600">enforced</div>
        </div>
      </div>

      {Object.keys(stats).length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] text-slate-600 mb-2">Protocol breakdown</div>
          {Object.entries(stats).sort((a, b) => b[1] - a[1]).map(([proto, count]) => {
            const color = PROTOCOL_COLORS[proto] || '#475569';
            const pct = total > 0 ? Math.round(count / total * 100) : 0;
            return (
              <div key={proto} className="mb-1.5">
                <div className="flex justify-between mb-0.5">
                  <span className="text-[11px] font-semibold" style={{ color }}>{proto.toUpperCase()}</span>
                  <span className="text-[10px] text-slate-600">{count} · {pct}%</span>
                </div>
                <div className="h-1 rounded bg-slate-100 overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${pct}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="border-t border-slate-200 pt-3">
        <div className="text-[11px] text-slate-600 mb-2">Recent events</div>
        {events.length === 0 ? (
          <div className="text-[11px] text-slate-500">No events in current window</div>
        ) : events.slice(0, 8).map((ev, i) => (
          <div key={ev.id || i} className="mb-1.5 p-2 rounded bg-slate-100/60 border border-slate-200">
            <div className="flex justify-between items-center mb-0.5">
              <span className="text-[10px] text-slate-600 font-mono">{fmtUtc(ev.timestamp)}</span>
              <span className="text-[10px]" style={{ color: ev.enforcement_event ? '#EF4444' : PROTOCOL_COLORS[(ev.protocol || '').toLowerCase()] || '#64748B' }}>
                {ev.enforcement_event ? '🛡 BLOCKED' : (ev.protocol || 'UNK').toUpperCase()}
              </span>
            </div>
            <div className="text-[11px] text-slate-700 leading-tight truncate">{ev.summary || ev.description}</div>
          </div>
        ))}
        {events.length > 8 && (
          <div className="text-[10px] text-slate-500 text-center pt-1">+{events.length - 8} more events</div>
        )}
      </div>
    </div>
  );
}

function EdgeEventDetail({ edgeSel, onClose }) {
  const { proto, events } = edgeSel;
  const color = PROTOCOL_COLORS[proto] || '#475569';
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold" style={{ color }}>{proto.toUpperCase()} Events</span>
        <button onClick={onClose} className="text-slate-600 hover:text-slate-700">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      {events.length === 0 ? (
        <div className="text-[11px] text-slate-500">No events for this path</div>
      ) : events.map(ev => (
        <div key={ev.id} className="mb-2 p-2 rounded bg-slate-100/60 border border-slate-200">
          <div className="flex justify-between items-center mb-0.5">
            <span className="text-[10px] font-semibold text-slate-600">{fmtUtc(ev.timestamp)}</span>
            <span className="text-[10px]" style={{ color: ev.enforcement_event ? '#EF4444' : '#475569' }}>
              {ev.enforcement_event ? '🛡 BLOCKED' : ev.severity}
            </span>
          </div>
          <div className="text-[11px] text-slate-700 leading-tight">{ev.summary}</div>
          {/* Protocol-specific fields */}
          {proto === 'goose' && ev.details?.source_mac && (
            <div className="text-[10px] text-yellow-700 font-mono mt-0.5">MAC: {ev.details.source_mac}</div>
          )}
          {proto === 'dnp3' && ev.details?.function_code && (
            <div className="text-[10px] text-blue-700 mt-0.5">FC: {ev.details.function_code}</div>
          )}
          {proto === 'mms' && ev.details?.service_type && (
            <div className="text-[10px] text-teal-700 mt-0.5">{ev.details.service_type}</div>
          )}
          {proto === 'modbus' && ev.details?.function_code && (
            <div className="text-[10px] text-green-700 mt-0.5">FC: {ev.details.function_code}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function ProtocolSummary({ events, gooseEvents }) {
  const stats = useMemo(() => protocolStats(events), [events]);
  return (
    <div className="p-4 space-y-4">
      <div className="text-xs font-semibold text-slate-700">Protocol Activity</div>
      {Object.entries(stats).sort((a, b) => b[1] - a[1]).map(([proto, count]) => {
        const color = PROTOCOL_COLORS[proto] || '#475569';
        const pct = events.length > 0 ? Math.round(count / events.length * 100) : 0;
        return (
          <div key={proto}>
            <div className="flex justify-between mb-0.5">
              <span className="text-[12px] font-semibold" style={{ color }}>{proto.toUpperCase()}</span>
              <span className="text-[11px] text-slate-600">{count} events · {pct}%</span>
            </div>
            <div className="h-1 rounded bg-slate-100 overflow-hidden">
              <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
      {Object.keys(stats).length === 0 && (
        <div className="text-[12px] text-slate-500">No protocol data in time window</div>
      )}
      <div className="pt-3 border-t border-slate-200 text-[11px] text-slate-500 leading-relaxed">
        Click any edge on the graph to inspect events for that protocol path.
        {gooseEvents.length > 0 && ' GOOSE renders as a protection domain bus — not point-to-point TCP.'}
      </div>
    </div>
  );
}
